/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    Component,
    EventEmitter,
    Input,
    SimpleChanges,
} from '@angular/core';

import { ClrDatagridFilterInterface } from '@clr/angular';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { Observable, Subject } from 'rxjs';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';
import { IAviDataGridRow, IDropdownFilter } from '../../avi-data-grid/avi-data-grid.types';
import './avi-data-grid-dropdown-filter.component.less';

/**
 * @description Component used to add a dropdown filter to a datagrid column. Both enum values and dropdown options are
 * supported.
 * @author alextsg
 */
@Component({
    selector: 'avi-data-grid-dropdown-filter',
    templateUrl: './avi-data-grid-dropdown-filter.component.html',
})
export class AviDataGridDropdownFilterComponent {
    @Input()
    public columnDropdownFilter: IDropdownFilter;

    public options: IAviDropdownOption[] = [];

    /**
     * The selected filter value.
     */
    public filter = '';

    /**
     * Controls closing of the dropdown menu.
     */
    public optionsControl$: Observable<boolean>;

    public readonly optionsPositionsPriority: ConnectedPosition[] = [{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: 15,
    }];

    /**
     * Passed to the datagrid filter directive. 'accepts' and 'changes' are not used since the displayed rows are
     * controlled externally, but isActive is used to render the active filter icon.
     */
    public clrDgFilter: ClrDatagridFilterInterface<IAviDataGridRow> = {
        accepts: () => true,
        isActive: () => Boolean(this.filter),
        changes: new EventEmitter(),
    };

    private optionsSubject = new Subject<boolean>();

    constructor(private readonly schemaService: SchemaService) {
        this.optionsControl$ = this.optionsSubject.asObservable();
    }

    /** @override */
    public ngOnInit(): void {
        this.setOptions();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.columnDropdownFilter) {
            this.setOptions();
        }
    }

    /**
     * Handle selection of an option from the dropdown.
     */
    public handleSelect(option: IAviDropdownOption): void {
        const { value } = option;

        if (this.filter === value) {
            this.filter = '';
            this.columnDropdownFilter.onSelect(null);
        } else {
            this.filter = value as string;
            this.columnDropdownFilter.onSelect(this.filter);
        }

        this.optionsSubject.next(false);
    }

    public closeOptions(): void {
        this.optionsSubject.next(false);
    }

    public trackByValue(index: number, option: IAviDropdownOption): string | number {
        return option.value;
    }

    /**
     * Handle click event to prevent propagating the event to the datagrid.
     */
    public handleClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    /**
     * Set options to be rendered in the dropdown.
     */
    private setOptions(): void {
        const { enum: enumType, dropdownOptions } = this.columnDropdownFilter;

        if (enumType) {
            const enumVals = this.schemaService.getEnumValues(enumType);

            this.options = enumVals.map(({ label, value, description }) => {
                return createDropdownOption(value, label, description || label);
            });
        } else {
            this.options = dropdownOptions;
        }
    }
}
